import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTH_URL = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingSession, setProcessingSession] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    checkExistingSession();
  }, []);

  // Process session_id from URL fragment
  useEffect(() => {
    const fragment = window.location.hash;
    console.log('[AUTH] Checking URL fragment:', fragment);
    
    if (fragment.includes('session_id=')) {
      const sessionId = new URLSearchParams(fragment.substring(1)).get('session_id');
      console.log('[AUTH] Found session_id in URL:', sessionId);
      
      if (sessionId) {
        processSessionId(sessionId);
      } else {
        console.error('[AUTH] session_id was empty!');
      }
    } else {
      console.log('[AUTH] No session_id in URL');
    }
  }, []);

  const checkExistingSession = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      console.log('No existing session');
    } finally {
      setLoading(false);
    }
  };

  const processSessionId = async (sessionId) => {
    console.log('[AUTH] Processing session_id:', sessionId);
    console.log('[AUTH] Backend API:', API);
    
    setProcessingSession(true);
    
    // Timeout protection: if processing takes more than 10 seconds, stop loading
    const timeout = setTimeout(() => {
      console.error('[AUTH] ⏱️ Session processing timeout - stopping loading');
      alert('Giriş işlemi zaman aşımına uğradı. Lütfen tekrar deneyin.');
      setProcessingSession(false);
      setLoading(false);
    }, 10000);
    
    try {
      console.log('[AUTH] Sending session to backend...');
      const response = await axios.post(
        `${API}/auth/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      
      console.log('[AUTH] ✅ Session validated, user:', response.data.user);
      setUser(response.data.user);
      
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      clearTimeout(timeout);
    } catch (error) {
      console.error('[AUTH] ❌ Error processing session:', error);
      console.error('[AUTH] Error details:', error.response?.data);
      
      // Show user-friendly error
      const errorMsg = error.response?.data?.detail || 'Giriş işlemi başarısız oldu';
      alert(`Hata: ${errorMsg}`);
      
      clearTimeout(timeout);
    } finally {
      setProcessingSession(false);
    }
  };

  const getRoleDashboard = (role) => {
    switch(role) {
      case 'admin':
        return '/admin/dashboard';
      case 'teacher':
        return '/teacher/dashboard';
      case 'parent':
        return '/parent/dashboard';
      case 'student':
      default:
        return '/dashboard';
    }
  };

  const login = () => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    const authRedirectUrl = `${AUTH_URL}/?react_app_backend_url=${encodeURIComponent(BACKEND_URL)}&redirect=${encodeURIComponent(redirectUrl)}`;
    
    console.log('[AUTH] Redirecting to:', authRedirectUrl);
    console.log('[AUTH] Current origin:', window.location.origin);
    console.log('[AUTH] Backend URL:', BACKEND_URL);
    console.log('[AUTH] Redirect URL:', redirectUrl);
    
    window.location.href = authRedirectUrl;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      window.location.href = '/';
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const completeRegistration = async (registrationData) => {
    try {
      const response = await axios.post(
        `${API}/auth/register`,
        registrationData,
        { withCredentials: true }
      );
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Error completing registration:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    processingSession,
    login,
    logout,
    completeRegistration,
    getRoleDashboard,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};